import { translations } from '@/i18n/translations';

/**
 * SEO Phase 2: German-locked SEO output.
 * All crawler-facing metadata resolves to German.
 * Change SEO_LANGUAGE when expanding public SEO to other languages.
 */
export const SEO_LANGUAGE = 'de' as const;

const DOMAIN = 'https://szoradi.com';
const t = translations[SEO_LANGUAGE];

// ---------- helpers ----------

/** Resolve an instrument field to the SEO language, not the session language. */
export function getSeoTranslated(
  instrument: {
    title?: string | null;
    description?: string | null;
    seo_title?: string | null;
    seo_description?: string | null;
    title_translations?: any;
    description_translations?: any;
  },
  field: 'title' | 'description',
): string {
  // 1. Explicit SEO field
  const seoField = field === 'title' ? instrument.seo_title : instrument.seo_description;
  if (seoField) return seoField;

  // 2. Translation in SEO language
  const translationsObj = field === 'title'
    ? instrument.title_translations
    : instrument.description_translations;
  if (translationsObj && typeof translationsObj === 'object') {
    const val = (translationsObj as Record<string, string>)[SEO_LANGUAGE];
    if (val) return val;
  }

  // 3. Raw field fallback
  return instrument[field] || '';
}

/** Get a navigation label locked to SEO language */
function seoNav(key: keyof typeof t): string {
  return t[key] as string;
}

// ---------- Schema builders ----------

export function buildOrganization(settings: {
  social_instagram?: string;
  social_facebook?: string;
  social_youtube?: string;
  contact_street?: string;
  contact_postal_code?: string;
  contact_city?: string;
  contact_country?: string;
  contact_email?: string;
  contact_phone?: string;
}) {
  const sameAs = [settings.social_instagram, settings.social_facebook, settings.social_youtube].filter(Boolean);
  const address: Record<string, string> = { '@type': 'PostalAddress' };
  if (settings.contact_street) address.streetAddress = settings.contact_street;
  if (settings.contact_city) address.addressLocality = settings.contact_city;
  if (settings.contact_postal_code) address.postalCode = settings.contact_postal_code;
  if (settings.contact_country) address.addressCountry = settings.contact_country;

  return {
    '@type': 'Organization',
    name: 'Szoradi Instruments',
    url: DOMAIN,
    description: t.metaDescription,
    image: `${DOMAIN}/og-image.jpg`,
    ...(Object.keys(address).length > 1 && { address }),
    ...(sameAs.length > 0 && { sameAs }),
    ...(settings.contact_email && { email: settings.contact_email }),
    ...(settings.contact_phone && { telephone: settings.contact_phone }),
  };
}

export function buildWebSite() {
  return {
    '@type': 'WebSite',
    name: 'Szoradi Instruments',
    url: DOMAIN,
    inLanguage: SEO_LANGUAGE,
  };
}

export function buildWebPage(path: string, name: string, description?: string) {
  return {
    '@type': 'WebPage',
    '@id': `${DOMAIN}${path}#webpage`,
    url: `${DOMAIN}${path}`,
    name,
    ...(description && { description }),
    inLanguage: SEO_LANGUAGE,
    isPartOf: { '@id': `${DOMAIN}#website` },
  };
}

export function buildBreadcrumbList(items: { name: string; path: string }[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: seoNav('navHome'), item: DOMAIN },
      ...items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: item.name,
        item: `${DOMAIN}${item.path}`,
      })),
    ],
  };
}

export function buildProduct(instrument: {
  title?: string | null;
  description?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  title_translations?: any;
  description_translations?: any;
  slug?: string | null;
  id: string;
  price?: number | null;
  currency?: string;
  price_display?: string;
  availability?: string;
  instrument_type?: string;
}, heroImageUrl?: string) {
  const name = getSeoTranslated(instrument, 'title');
  const description = getSeoTranslated(instrument, 'description');
  const path = `/instruments/${instrument.slug || instrument.id}`;

  const availabilityMap: Record<string, string> = {
    available: 'https://schema.org/InStock',
    reserved: 'https://schema.org/LimitedAvailability',
    sold: 'https://schema.org/SoldOut',
  };

  const offers: Record<string, any> = {
    '@type': 'Offer',
    url: `${DOMAIN}${path}`,
    availability: availabilityMap[instrument.availability || ''] || 'https://schema.org/InStock',
    priceCurrency: instrument.currency || 'EUR',
  };

  if (instrument.price_display === 'show' && instrument.price) {
    offers.price = instrument.price;
  }

  return {
    '@type': 'Product',
    name,
    ...(description && { description: description.slice(0, 300) }),
    url: `${DOMAIN}${path}`,
    ...(heroImageUrl && { image: heroImageUrl }),
    brand: { '@type': 'Brand', name: 'Szoradi Instruments' },
    ...(instrument.instrument_type && { category: instrument.instrument_type }),
    offers,
  };
}

export function buildCreativeWork(instrument: {
  title?: string | null;
  description?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  title_translations?: any;
  description_translations?: any;
  slug?: string | null;
  id: string;
  year?: number | null;
  instrument_type?: string;
}, heroImageUrl?: string) {
  const name = getSeoTranslated(instrument, 'title');
  const description = getSeoTranslated(instrument, 'description');
  const path = `/instruments/${instrument.slug || instrument.id}`;

  return {
    '@type': 'CreativeWork',
    name,
    ...(description && { description: description.slice(0, 300) }),
    url: `${DOMAIN}${path}`,
    ...(heroImageUrl && { image: heroImageUrl }),
    creator: { '@type': 'Organization', name: 'Szoradi Instruments' },
    ...(instrument.year && { dateCreated: String(instrument.year) }),
    ...(instrument.instrument_type && { genre: instrument.instrument_type }),
  };
}

/** Wrap multiple schema objects into a single @graph block */
export function buildGraph(...items: Record<string, any>[]) {
  return {
    '@context': 'https://schema.org',
    '@graph': items,
  };
}
